import { Outlet, Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import PwaInstallHint from "@/components/PwaInstallHint";
import AccountMenu from "@/components/AccountMenu";

// Field Notes shell. Values come from the header and footer of
// "Landing - Field Notes.dc.html": hard 1px ink rules top and bottom, an 8px
// accent dot before the masthead, and uppercase Archivo throughout.

// Nav links: Archivo 600, 12px, letter-spacing .14em, uppercase.
// The README says the active link is "in accent", but the accent is 3.4:1 and
// fidelity rule 2 forbids accent as a text colour, so the active state uses
// accent-text, which is the readable equivalent.
const navLink = ({ isActive }: { isActive: boolean }) =>
  [
    "inline-flex items-center min-h-[44px] font-sans font-semibold text-xs uppercase tracking-[0.14em] transition-colors",
    isActive ? "text-accent-text" : "text-ink hover:text-accent-text",
  ].join(" ");

const Masthead = ({ className = "" }: { className?: string }) => (
  <Link to="/" className={`flex items-center gap-2.5 min-h-[44px] ${className}`}>
    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-accent shrink-0" />
    <span className="font-sans font-bold text-sm uppercase tracking-[0.32em] text-ink whitespace-nowrap">
      Sunday Atlas
    </span>
  </Link>
);

const Layout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  // 36px visual circle on desktop, inside a 44px hit box so mobile clears the
  // tap-target rule without the control growing.
  const ThemeToggle = () => (
    <button
      type="button"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      aria-label="Toggle theme"
      title="Toggle theme"
      className="inline-flex h-11 w-11 items-center justify-center -mr-2 text-ink"
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-paper-hover transition-colors">
        <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      </span>
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <PwaInstallHint />

      <header className="sticky top-0 z-50 bg-[color-mix(in_srgb,var(--fn-paper)_95%,transparent)] backdrop-blur-xl border-b border-ink">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 py-4 flex items-center justify-between gap-4">
          <Masthead />

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-[22px]">
            <NavLink to="/dashboard" className={navLink}>
              My trips
            </NavLink>
            <NavLink
              to="/journey/new"
              className="inline-flex items-center min-h-[44px] px-[18px] rounded-full border border-ink font-sans font-semibold text-xs uppercase tracking-[0.14em] text-ink hover:bg-ink hover:text-paper transition-colors"
            >
              Start a trip
            </NavLink>
            <ThemeToggle />
            <AccountMenu />
          </nav>

          {/* Mobile */}
          <div className="md:hidden flex items-center">
            <ThemeToggle />
            <button
              className="inline-flex h-11 w-11 items-center justify-center text-ink"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-paper border-t border-rule px-5 py-2 animate-fade-in">
            <nav className="flex flex-col">
              <NavLink to="/dashboard" className={navLink} onClick={() => setIsMenuOpen(false)}>
                My trips
              </NavLink>
              <NavLink to="/journey/new" className={navLink} onClick={() => setIsMenuOpen(false)}>
                Start a trip
              </NavLink>
              <div className="py-2 border-t border-rule-hair mt-1" onClick={() => setIsMenuOpen(false)}>
                <AccountMenu />
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-ink">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 py-7 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-accent shrink-0" />
            <span className="font-sans font-bold text-[11px] uppercase tracking-[0.28em] text-ink">
              Sunday Atlas
            </span>
          </div>

          <nav className="flex items-center gap-6 font-sans font-semibold text-[11px] uppercase tracking-[0.14em] text-ink-muted">
            <Link to="/" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] hover:text-ink transition-colors">
              Home
            </Link>
            <Link to="/dashboard" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] hover:text-ink transition-colors">
              My trips
            </Link>
            <Link to="/privacy" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] hover:text-ink transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] hover:text-ink transition-colors">
              Terms
            </Link>
          </nav>

          {/* The byline link stays inline: WCAG 2.5.5 exempts targets inside a
              sentence, and padding it to 44px would break the line of text. */}
          <span className="font-serif text-xs text-ink-faint">
            © {new Date().getFullYear()} · by{" "}
            <a
              href="https://alxnhfr-bit.github.io/portfolio/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-accent-text transition-colors"
            >
              Alexander Neuhofer
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
